import { SuggestionRow } from "~/components/SuggestionRow"
import { EmptyState } from "~/components/screens/StateScreen"

import { MessageCircleIcon, ThumbsUpIcon } from "lucide-react"
import { type FormEvent, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Link } from "~/components/Link"
import { toast } from "sonner"
import { StatusIcon } from "~/components/icons/status"
import { ListSection } from "~/components/SectionHeader"
import { RoleBadge } from "~/components/RoleBadge"
import {
	ActivityCommentCard,
	CommentComposer
} from "~/components/activity/Comments"
import { LoginPanel } from "~/components/LoginPanel"
import {
	captureFeatureFlagInteraction,
	captureFeatureFlagView
} from "~/components/providers/Posthog"
import { LoadingState } from "~/components/screens/StateScreen"
import { Badge } from "~/components/ui/badge"
import { SettingsForm } from "~/components/settings/SettingsForm"
import { Button } from "~/components/ui/button"
import { Input } from "~/components/ui/input"
import { Textarea } from "~/components/ui/textarea"
import { authClient } from "~/lib/auth-client"
import { getWebAuthProxyStartUrl, getWebAuthUrls } from "~/lib/client-url"
import { IssueStatus } from "~/lib/enums"
import { PublicSuggestionPermissions } from "~/lib/permissions"
import { enumFormat, shortRelativeTime, textToEditorContent } from "~/lib/utils"
import { api } from "~/trpc/react"

const currentBoardInput = (orgSlug?: string, teamSlug?: string) => {
	const host = window.location.host
	return orgSlug && teamSlug
		? { organizationSlug: orgSlug, teamSlug }
		: { host }
}

function SuggestionsUnavailable() {
	return (
		<div className="mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center gap-2 p-6 text-center">
			<h1 className="text-2xl font-semibold">Suggestions unavailable</h1>
			<p className="text-muted-foreground">
				This suggestion board is not available right now.
			</p>
		</div>
	)
}

export function PublicSuggestionsPage({ customDomain = false }) {
	const { orgSlug, teamSlug } = useParams<{
		orgSlug?: string
		teamSlug?: string
	}>()
	const boardInput = currentBoardInput(
		customDomain ? undefined : orgSlug,
		customDomain ? undefined : teamSlug
	)
	const {
		data: board,
		isLoading: isBoardLoading,
		error: boardError
	} = api.suggestions.publicBoard.useQuery(boardInput, { retry: false })
	const { data: suggestions, isLoading } = api.suggestions.publicList.useQuery(
		boardInput,
		{ enabled: !!board }
	)

	useEffect(() => {
		if (board) captureFeatureFlagView("suggestions")
	}, [board])

	if (isBoardLoading || isLoading) {
		return <LoadingState className="min-h-screen" />
	}
	if (boardError) return <SuggestionsUnavailable />
	if (!board) return null

	const basePath = customDomain ? "" : `/s/${orgSlug}/${teamSlug}`
	const canSubmit =
		(board.publicPermissions & PublicSuggestionPermissions.SUBMIT) ===
		PublicSuggestionPermissions.SUBMIT
	const groups = Object.values(IssueStatus)
		.map((status) => ({
			id: status,
			name: enumFormat(status),
			items: (suggestions ?? [])
				.filter((suggestion) => suggestion.status === status)
				.sort(
					(a, b) =>
						b.voteCount - a.voteCount ||
						b.createdAt.getTime() - a.createdAt.getTime()
				)
		}))
		.filter((group) => group.items.length > 0)

	return (
		<div className="mx-auto flex min-h-screen max-w-4xl flex-col gap-6 p-6">
			<header className="flex flex-wrap items-center justify-between gap-4">
				<div>
					<h1 className="text-3xl font-semibold">{board.title}</h1>
					{board.description && (
						<p className="text-muted-foreground">{board.description}</p>
					)}
				</div>
				{canSubmit && (
					<Button asChild>
						<Link to={`${basePath}/new`}>New suggestion</Link>
					</Button>
				)}
			</header>

			<div className="flex flex-col gap-4">
				{groups.map((group) => (
					<ListSection
						key={group.id}
						variant="plain"
						icon={<StatusIcon status={group.id} />}
						title={group.name}
						count={group.items.length}
						itemName="suggestion"
					>
						<div className="divide-y divide-border">
							{group.items.map((suggestion) => (
								<SuggestionRow
									key={suggestion.id}
									suggestion={suggestion}
									to={`${basePath}/${suggestion.friendlyId}`}
									variant="public"
								/>
							))}
						</div>
					</ListSection>
				))}
				{suggestions?.length === 0 && (
					<EmptyState scope="inline" title="No suggestions yet." />
				)}
			</div>
		</div>
	)
}

export function PublicSuggestionNewPage({ customDomain = false }) {
	const { orgSlug, teamSlug } = useParams<{
		orgSlug?: string
		teamSlug?: string
	}>()
	const navigate = useNavigate()
	const { data: session } = authClient.useSession()
	const utils = api.useUtils()
	const boardInput = currentBoardInput(
		customDomain ? undefined : orgSlug,
		customDomain ? undefined : teamSlug
	)
	const {
		data: board,
		error: boardError,
		isLoading: isBoardLoading
	} = api.suggestions.publicBoard.useQuery(boardInput, { retry: false })
	const create = api.suggestions.publicCreate.useMutation({
		onSuccess: (suggestion) => {
			if (!suggestion) return
			captureFeatureFlagInteraction("suggestions")
			void utils.suggestions.publicList.invalidate(boardInput)
			void utils.suggestions.publicGet.invalidate({
				...boardInput,
				friendlyId: String(suggestion.friendlyId)
			})
			toast.success("Suggestion submitted")
			navigate(
				customDomain
					? `/${suggestion.friendlyId}`
					: `/s/${orgSlug}/${teamSlug}/${suggestion.friendlyId}`
			)
		},
		onError: (error) => toast.error(error.message)
	})
	useEffect(() => {
		if (board) captureFeatureFlagView("suggestions")
	}, [board])

	const canSubmit =
		board &&
		(board.publicPermissions & PublicSuggestionPermissions.SUBMIT) ===
			PublicSuggestionPermissions.SUBMIT

	if (isBoardLoading) return <LoadingState className="min-h-screen" />
	if (boardError) return <SuggestionsUnavailable />

	return (
		<div className="mx-auto flex min-h-screen max-w-2xl flex-col gap-6 p-6">
			<Link
				to={customDomain ? "/" : `/s/${orgSlug}/${teamSlug}`}
				className="text-sm text-muted-foreground hover:text-foreground"
			>
				← Suggestions
			</Link>
			<h1 className="text-3xl font-semibold">New suggestion</h1>
			{canSubmit && !session ? (
				<LoginPanel callbackURL={window.location.href} />
			) : canSubmit ? (
				<form
					className="grid gap-4"
					onSubmit={(event: FormEvent<HTMLFormElement>) => {
						event.preventDefault()
						const formData = new FormData(event.currentTarget)
						create.mutate({
							...boardInput,
							title: formData.get("title")?.toString() || "",
							description: formData.get("description")?.toString() || undefined
						})
					}}
				>
					<Input name="title" placeholder="Suggestion title" required />
					<Textarea name="description" placeholder="What should change?" />
					<Button
						type="submit"
						disabled={create.isPending}
						pending={create.isPending}
					>
						Submit
					</Button>
				</form>
			) : (
				<p className="text-muted-foreground">
					Submitting suggestions is disabled.
				</p>
			)}
		</div>
	)
}

export function PublicSuggestionDetailPage({ customDomain = false }) {
	const { orgSlug, teamSlug, suggestionFriendlyId } = useParams<{
		orgSlug?: string
		teamSlug?: string
		suggestionFriendlyId?: string
	}>()
	const { data: session } = authClient.useSession()
	const [commentText, setCommentText] = useState(textToEditorContent(null))
	const boardInput = currentBoardInput(
		customDomain ? undefined : orgSlug,
		customDomain ? undefined : teamSlug
	)
	const friendlyId = customDomain
		? window.location.pathname.split("/").filter(Boolean)[0]
		: suggestionFriendlyId
	const utils = api.useUtils()
	const {
		data: board,
		error: boardError,
		isLoading: isBoardLoading
	} = api.suggestions.publicBoard.useQuery(boardInput, { retry: false })
	const { data: suggestion, isLoading } = api.suggestions.publicGet.useQuery(
		{ ...boardInput, friendlyId: friendlyId ?? "" },
		{ enabled: !!friendlyId && !!board }
	)
	const vote = api.suggestions.publicToggleVote.useMutation({
		onSuccess: () => {
			captureFeatureFlagInteraction("suggestions")
			void utils.suggestions.publicGet.invalidate({
				...boardInput,
				friendlyId: friendlyId ?? ""
			})
		},
		onError: (error) => toast.error(error.message)
	})
	const comment = api.suggestions.publicComment.useMutation({
		onSuccess: () => {
			captureFeatureFlagInteraction("suggestions")
			void utils.suggestions.publicGet.invalidate({
				...boardInput,
				friendlyId: friendlyId ?? ""
			})
		},
		onError: (error) => toast.error(error.message)
	})
	const editOwn = api.suggestions.publicEditOwn.useMutation({
		onSuccess: () => {
			captureFeatureFlagInteraction("suggestions")
			void utils.suggestions.publicGet.invalidate({
				...boardInput,
				friendlyId: friendlyId ?? ""
			})
			toast.success("Suggestion updated")
		},
		onError: (error) => toast.error(error.message)
	})

	useEffect(() => {
		if (suggestion) captureFeatureFlagView("suggestions")
	}, [suggestion])

	if (isBoardLoading || isLoading) {
		return <LoadingState className="min-h-screen" />
	}
	if (boardError) return <SuggestionsUnavailable />
	if (!suggestion || !board) return null

	const canVote =
		(board.publicPermissions & PublicSuggestionPermissions.VOTE) ===
		PublicSuggestionPermissions.VOTE
	const canComment =
		(board.publicPermissions & PublicSuggestionPermissions.COMMENT) ===
		PublicSuggestionPermissions.COMMENT
	const canEditOwn =
		(board.publicPermissions & PublicSuggestionPermissions.EDIT_OWN) ===
			PublicSuggestionPermissions.EDIT_OWN &&
		session?.user.id === suggestion.authorUserId

	return (
		<div className="mx-auto flex min-h-screen max-w-3xl flex-col gap-6 p-6">
			<Link
				to={customDomain ? "/" : `/s/${orgSlug}/${teamSlug}`}
				className="text-sm text-muted-foreground hover:text-foreground"
			>
				← Suggestions
			</Link>
			<div className="rounded-lg border bg-background p-5">
				<div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
					<Badge variant="secondary">
						{enumFormat(suggestion.displayStatus)}
					</Badge>
				</div>
				<h1 className="text-3xl font-semibold">{suggestion.title}</h1>
				<p className="mt-4 whitespace-pre-wrap text-muted-foreground">
					{suggestion.description || "No description"}
				</p>
				<div className="mt-6 flex flex-wrap items-center gap-3">
					<Button
						variant={suggestion.userVoted ? "default" : "outline"}
						disabled={!canVote}
						onClick={() => {
							if (!session) {
								const authUrls = getWebAuthUrls(window.location.href)
								const proxyUrl = getWebAuthProxyStartUrl({
									provider: "discord",
									callbackURL: authUrls.callbackURL,
									errorCallbackURL: authUrls.errorCallbackURL
								})
								if (proxyUrl) {
									window.location.assign(proxyUrl)
									return
								}
								void authClient.signIn.social({
									provider: "discord",
									callbackURL: authUrls.callbackURL,
									errorCallbackURL: authUrls.errorCallbackURL
								})
								return
							}
							vote.mutate({ ...boardInput, suggestionId: suggestion.id })
						}}
					>
						<ThumbsUpIcon className="size-4" /> {suggestion.voteCount}
					</Button>
					{suggestion.linkedIssue && (
						<span className="text-sm text-muted-foreground">
							Linked issue #{suggestion.linkedIssue.friendlyId}
						</span>
					)}
				</div>
			</div>

			{canEditOwn && (
				<SettingsForm
					isSubmitting={editOwn.isPending}
					className="grid gap-3 rounded-lg border bg-background p-5"
					onSave={async (form) => {
						const formData = new FormData(form)
						await editOwn.mutateAsync({
							...boardInput,
							suggestionId: suggestion.id,
							title: formData.get("title")?.toString() || suggestion.title,
							description: formData.get("description")?.toString() || undefined
						})
					}}
				>
					<h2 className="text-lg font-medium">Edit your suggestion</h2>
					<Input name="title" defaultValue={suggestion.title} />
					<Textarea
						name="description"
						defaultValue={suggestion.description ?? ""}
					/>
				</SettingsForm>
			)}

			<div className="grid gap-3 rounded-lg border bg-background p-5">
				<h2 className="flex items-center gap-2 text-lg font-medium">
					<MessageCircleIcon className="size-4" /> Comments
				</h2>
				{canComment && session && (
					<CommentComposer
						value={commentText}
						onChange={setCommentText}
						onSubmit={async () => {
							await comment.mutateAsync({
								...boardInput,
								suggestionId: suggestion.id,
								content: commentText
							})
						}}
						placeholder="Write a comment..."
						isPending={comment.isPending}
						submitLabel="Comment"
					/>
				)}
				{canComment && !session && (
					<LoginPanel callbackURL={window.location.href} />
				)}
				{suggestion.comments.map((item) => (
					<ActivityCommentCard
						key={item.id}
						id={`comment-${item.id}`}
						content={item.content}
						authorName={item.authorName ?? "Unknown"}
						authorImage={item.authorImage}
						createdAt={item.createdAt}
						formatRelativeTimestamp={(date) => `${shortRelativeTime(date)} ago`}
						badges={
							item.authorHighestRole ? (
								<RoleBadge role={item.authorHighestRole} />
							) : null
						}
					/>
				))}
			</div>
		</div>
	)
}
